import styled, { createGlobalStyle } from 'styled-components';
import { ToastContainer } from 'react-toastify';
import { normalize } from 'styled-normalize';
import Colors from 'library/constants/colors';
import Fonts from 'library/constants/fonts';
import DefaultToasterStyles from 'library/constants/toast-style-default';
import { ActionIconContainer } from 'library/styles/icon';
import { InputText } from 'library/styles/text';
import { TransitionConfig, ShadowLayout } from '../constants/styles-config';

const themeOptions = {
  colors: {
    shade08: Colors.shade08,
    shade07: Colors.shade07,
    shade06: Colors.shade06,
    shade05: Colors.shade05,
    shade04: Colors.shade04,
    shade03: Colors.shade03,
    shade02: Colors.shade02,
    violet: Colors.violet,
    violetLight: Colors.violetLight,
    blue: Colors.blue,
    success: Colors.success,
    error: Colors.error,
    warning: Colors.warning,
  },
  fonts: {
    icon: Fonts.icon,
  },
  defToastStyles: {
    defStyles: DefaultToasterStyles,
  },
};

const StyledToastContainer = styled(ToastContainer).attrs({
  className: 'toast-container',
  bodyClassName: 'body',
  progressClassName: 'progress',
})`
   
  z-index: 1500;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;

  .red {
    background: ${Colors.error};
    ${themeOptions.defToastStyles.defStyles}
  }
  
  .green {
    background: ${Colors.success};
    ${themeOptions.defToastStyles.defStyles}
  }

  .yellow {
    background: ${Colors.warning};
    ${themeOptions.defToastStyles.defStyles}
  }
  
  button[aria-label="close"] {
    margin: 13px 13px 0 0;
    color: ${Colors.shade08};
    opacity: 1; 
  }

  .progress {
    background: #F0DBDB;
    height: 6px;
    margin-left: 5px;
  }

  .body {
    padding: 10px 10px 10px 24px;
  }
`;

const GlobalOverflowStyle = createGlobalStyle`
  body {
    overflow: ${({ isOverlayDisplayed }) => (!isOverlayDisplayed ? 'auto' : 'hidden !important')};
    cursor: ${({ isClickable }) => (isClickable ? 'inherit' : 'wait')};

    > * {
      pointer-events: ${({ isClickable }) => (isClickable ? 'auto' : 'none')};
    }
  }
  
  .ReactModal__Overlay {
    opacity: 0;
    transition: opacity ${TransitionConfig};
  }

  .ReactModal__Overlay--after-open{
    opacity: 1;
  }

  .ReactModal__Overlay--before-close{
    opacity: 0;
  }
`;

const GlobalStyle = createGlobalStyle`
  ${normalize}
  
  body {
    color: ${Colors.shade02};
    background-color: ${Colors.shade07};
    font-family: Lato, sans-serif;
    font-size: 14px;
    overflow: auto;
    overflow-y: ${({ isScrollable }) => (isScrollable ? 'inherit' : 'hidden !important')};
    min-width: 526px;

    &.ReactModal__Body--open {
      overflow: hidden;
    }

  }

  .Overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.75);
    z-index: 1000;
    overflow-y: auto;
  }

  .slide-enter {
    opacity: 0;
    transform: translateX(100%);
  }
  .slide-enter-active {
    opacity: 1;
    transform: translateX(0);
    transition: opacity 300ms, transform 400ms;
  }
  .slide-exit {
    opacity: 1;
  }
  .slide-exit-active {
    opacity: 0;
    transform: translateX(100%);
    transition: opacity 400ms, transform 400ms;
  }
  
  .fade-enter {
    opacity: 0;
  }
  .fade-enter-active {
    opacity: 1;
    transition: opacity 300ms;
  }
  .fade-exit {
    opacity: 1;
  }
  .fade-exit-active {
    opacity: 0;
    transition: opacity 300ms;
  }
  
  a, ${ActionIconContainer} {
    color: ${({ theme }) => theme.colors.violet};
    text-decoration: none;
  }
  
  input {
    ${InputText};
  }
  
  p {
    font-weight: normal;
    margin-bottom: 12px;
    margin-top: 13px;
  }

  ::-webkit-input-placeholder {
    color: ${Colors.shade03};
  }
  ::-moz-placeholder {
    color: ${Colors.shade03};
  }
  :-ms-input-placeholder {
    color: ${Colors.shade03};
  }
  :-moz-placeholder {
    color: ${Colors.shade03};
  }
  


  .react-tags-readonly {
    outline: none;
    background-image: linear-gradient(to right, #fff 50%, ${Colors.violet} 50%);
    background-position: inherit;
    transition: none;
    color: ${Colors.shade01};
    margin-bottom: 1px;
    pointer-events: none;
    cursor: not-allowed;

    .react-tags__selected {
      display: flex;
      flex-wrap: wrap;
    }


    &:hover {
      box-shadow: 0 2px 0 ${Colors.shade04}
    }
    z-index: 5;
  }
  
  .react-tags {
    background-color: ${Colors.shade07};
    transition: box-shadow ${TransitionConfig};

    .react-tags__selected {
      display: flex;
      flex-wrap: wrap;
    }
    
    &:hover {
      box-shadow: 0 2px 0 ${({ hasError }) => (hasError ? Colors.error : Colors.shade04)};
    }
  }
  
  .react-tags-readonly, .react-tags {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    flex-wrap: wrap;
    min-height: 56px;
    box-shadow: 0 2px 0 ${Colors.shade06};
    border-radius: 8px;
    border: none;
    cursor: pointer;
    padding: 10px 20px 10px 52px;
    margin-bottom: 10px;
    background-size: 200% 100%;
    position: relative;
    box-sizing: border-box;

    input::placeholder {
      color: ${Colors.shade01};
    }

    &:focus {
      box-shadow: 0 2px 0 ${Colors.violet};
      outline: none;
    }
  }

  .react-tags.is-focused {
    border-color: #B1B1B1;
  }

  .react-tags__selected {
    display: inline;
  }

  .react-tags__selected-tag {
    display: inline-block;
    box-sizing: border-box;
    margin: 0 6px 6px 0;
    padding: 6px 8px;
    border: 1px solid #D1D1D1;
    border-radius: 2px;
    background: #F1F1F1;

    /* match the font styles */
    font-size: inherit;
    line-height: inherit;
  }

  .react-tags__selected-tag:after {
    content: '\\2715';
    color: #AAA;
    margin-left: 8px;
  }

  .react-tags__selected-tag:hover,
  .react-tags__selected-tag:focus {
    border-color: #B1B1B1;
  }

  .react-tags__search {
    display: inline-block;

    /* match tag layout */
    padding: 5px 2px 4px;
    margin-top: 3px;
    margin-bottom: 3px;

    /* prevent autoresize overflowing the container */
    max-width: 100%;
  }

  .react-tags-readonly .react-tags__search {
    display: none;
  }

  @media screen and (min-width: 30em) {

    .react-tags__search {
      /* this will become the offsetParent for suggestions */
      //position: relative;
    }

  }

  .react-tags__search-input {
    background: transparent;
    border: none;
    
    &:active, &:focus, &:focus-visible {
      outline: none;
      border: none;
    }
  }

  .react-tags__search-input::-ms-clear {
    display: none;
  }

  .react-tags__suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 12;
    margin: 0;
    box-shadow: ${ShadowLayout};
  }

  @media screen and (min-width: 30em) {

    .react-tags__suggestions {
      //width: 240px;
    }

  }

  .react-tags__suggestions ul {
    width: 100%;
    //min-height: 100px;
    box-sizing: border-box;
    position: relative;
    background-color: ${Colors.shade08};
    border-radius: 0 0 24px 24px;
    border-top: 1px solid ${Colors.shade06};
    //z-index: 20;
    list-style-type: none;
    margin: 0;
    padding: 0;
    //padding-left: 52px;
  }

  .react-tags__suggestions li {
    width: 100%;
    height: 40px;
    background-color: ${Colors.shade08};
    line-height: 40px;
    cursor: pointer;

    &:last-child:not(:only-child) {
      margin-bottom: 15px;
      border-radius: 0 0 24px 24px;
    }
  }

  .react-tags__suggestions li mark {
    text-decoration: underline;
  }

  .react-tags__suggestions li:hover {
    cursor: pointer;
    background-color: ${Colors.shade07};
  }
  
  .react-tags__suggestions li span {
    margin-left: 52px;
  }
  
  .react-tags__suggestions li.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export {
  GlobalStyle,
  themeOptions,
  StyledToastContainer,
  GlobalOverflowStyle,
};
